import { ERouteName } from '@/typescript/enums/ERouteName';
import ProductSelectionView from '@/views/ProductSelectionView.vue';
import LoginView from '@/views/LoginView.vue';
import { useAuth } from '@/composables/useAuth';
import { useAuthStore } from '@/store/auth.store';
import { isKnownClientSlug } from '@/clients/clientRegistry';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/login',
            name: ERouteName.Login,
            component: LoginView,
        },
        {
            path: '/admin',
            name: ERouteName.Admin,
            component: () => import('@/views/AdminView.vue'),
        },
        {
            path: '/admin/configurator/:productName',
            name: ERouteName.ConfiguratorAdmin,
            component: () => import('@/views/EditorView.vue'),
        },
        {
            path: '/admin/configurator/:productName/:templateName',
            name: ERouteName.ConfiguratorAdminTemplate,
            component: () => import('@/views/EditorView.vue'),
        },
        {
            path: '/:clientSlug?',
            children: [
                {
                    path: '',
                    name: ERouteName.ProductSelection,
                    component: ProductSelectionView,
                },
                {
                    path: 'configurator/:productName',
                    name: ERouteName.Configurator,
                    component: () => import('@/views/EditorView.vue'),
                },
                {
                    path: 'configurator/:productName/:templateName',
                    name: ERouteName.ConfiguratorTemplate,
                    component: () => import('@/views/EditorView.vue'),
                },
            ],
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/',
        },
    ],
});

const ADMIN_ROUTES = [ERouteName.Admin, ERouteName.ConfiguratorAdmin, ERouteName.ConfiguratorAdminTemplate];

const CLIENT_ROUTES = [ERouteName.ProductSelection, ERouteName.Configurator, ERouteName.ConfiguratorTemplate];

function getClientSlugParam(slug: string | string[] | undefined): string {
    return typeof slug === 'string' ? slug : '';
}

router.beforeEach(async (to, from) => {
    const authStore = useAuthStore();
    const { checkAuth } = useAuth();

    await checkAuth();

    if (ADMIN_ROUTES.includes(to.name as ERouteName) && !authStore.isAuthenticated) {
        return { name: ERouteName.Login };
    }

    if (!CLIENT_ROUTES.includes(to.name as ERouteName)) {
        return undefined;
    }

    const targetSlug = getClientSlugParam(to.params.clientSlug);

    if (targetSlug.length > 0 && !isKnownClientSlug(targetSlug)) {
        return { ...to, params: { ...to.params, clientSlug: '' } };
    }

    const previousSlug = getClientSlugParam(from.params.clientSlug);

    if (targetSlug.length === 0 && previousSlug.length > 0) {
        return { ...to, params: { ...to.params, clientSlug: previousSlug } };
    }

    return undefined;
});

export { router };
